import { create } from "zustand";
import { produce } from "immer";
import type { CommonsStats, Config, Role, ServerMessage, Session, SymbolId } from "./types";

type Phase = "boot" | "consent" | "queue" | "playing" | "waiting" | "result" | "ended";

type CurrentRoom = {
  roomId: string;
  role: Role;
  round: number;
  maxDepth: number;
  opponentTag: string;
  startAt: number;
};

type ResultMessage = Extract<ServerMessage, { type: "round_result" }>;

export type AppState = {
  phase: Phase;
  config?: Config;
  session?: Session;
  clientId?: string;
  socket?: WebSocket;
  current?: CurrentRoom;
  result?: ResultMessage;
  partnerSubmitted: boolean;
  queueSize: number;
  chain: SymbolId[];
  confidence: number[];
  maxDepthPreference: number;
  stats?: CommonsStats;
  error?: string;
  setConfig: (config: Config) => void;
  setSession: (session: Session) => void;
  setSocket: (socket: WebSocket | undefined) => void;
  setMaxDepthPreference: (depth: number) => void;
  handleServerMessage: (message: ServerMessage) => void;
  setChoice: (level: number, symbol: SymbolId) => void;
  setConfidence: (level: number, confidence: number) => void;
  resetRound: () => void;
  setStats: (stats: CommonsStats) => void;
  setError: (error?: string) => void;
};

function defaultChain(depth: number): SymbolId[] {
  return Array.from({ length: depth + 1 }, () => "lantern" as SymbolId);
}

function defaultConfidence(depth: number): number[] {
  return Array.from({ length: depth + 1 }, (_, index) => (index === 0 ? 100 : 55));
}

export const useAppStore = create<AppState>((set, get) => ({
  phase: "boot",
  partnerSubmitted: false,
  queueSize: 0,
  chain: defaultChain(4),
  confidence: defaultConfidence(4),
  maxDepthPreference: 4,
  setConfig: (config) => set({ config, phase: "consent" }),
  setSession: (session) => set({ session }),
  setSocket: (socket) => set({ socket }),
  setMaxDepthPreference: (depth) =>
    set(
      produce((state: AppState) => {
        state.maxDepthPreference = Math.max(2, Math.min(6, Math.floor(depth)));
        if (!state.current) {
          state.chain = defaultChain(state.maxDepthPreference);
          state.confidence = defaultConfidence(state.maxDepthPreference);
        }
      })
    ),
  handleServerMessage: (message) =>
    set(
      produce((state: AppState) => {
        if (message.type === "hello") {
          state.clientId = message.clientId;
          state.phase = "queue";
        }
        if (message.type === "queued") {
          state.queueSize = message.queueSize;
          state.phase = "queue";
        }
        if (message.type === "matched") {
          state.current = { roomId: message.roomId, role: message.role, round: message.round, maxDepth: message.maxDepth, opponentTag: message.opponentTag, startAt: performance.now() };
          state.chain = defaultChain(message.maxDepth);
          state.confidence = defaultConfidence(message.maxDepth);
          state.result = undefined;
          state.partnerSubmitted = false;
          state.phase = "playing";
        }
        if (message.type === "partner_submitted") state.partnerSubmitted = true;
        if (message.type === "round_result") {
          state.result = message;
          state.phase = "result";
          state.partnerSubmitted = false;
        }
        if (message.type === "ended") {
          state.phase = "ended";
          state.error = message.reason;
          state.current = undefined;
        }
        if (message.type === "stats") state.stats = message.stats;
        if (message.type === "error") state.error = message.message;
      })
    ),
  setChoice: (level, symbol) =>
    set(
      produce((state: AppState) => {
        state.chain[level] = symbol;
      })
    ),
  setConfidence: (level, confidence) =>
    set(
      produce((state: AppState) => {
        state.confidence[level] = confidence;
      })
    ),
  resetRound: () => {
    const depth = get().current?.maxDepth ?? get().maxDepthPreference;
    set({ chain: defaultChain(depth), confidence: defaultConfidence(depth), result: undefined, partnerSubmitted: false });
  },
  setStats: (stats) => set({ stats }),
  setError: (error) => set({ error })
}));
